import { useEffect, useState } from "react";
import axios from "axios";
import { FAQS } from "./FAQ";

const SCHEMA_ID = "justudy-jsonld";
const SITE = "https://library-cowork-hub.preview.emergentagent.com";
const PHONE = "+918447496808";
const EMAIL = "justudylibrary@gmail.com";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_library-cowork-hub/artifacts/peeezf8j_WhatsApp%20Image%202026-06-11%20at%2013.15.27%20%281%29.jpeg";
const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_library-cowork-hub/artifacts/xm1adlbp_WhatsApp%20Image%202026-06-11%20at%2013.57.41%20%281%29.jpeg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEARBY_AREAS = [
  "Sector 15 Noida", "Sector 16 Noida", "Sector 18 Noida",
  "Sector 19 Noida", "Sector 25 Noida", "Sector 27 Noida",
  "Sector 29 Noida", "Sector 12 Noida", "Sector 11 Noida",
  "Atta Market Noida", "Botanical Garden Noida", "DLF Mall of India",
];

function buildLocalBusiness(branch) {
  return {
    "@type": ["LocalBusiness", "EducationalOrganization", "Library"],
    "@id": `${SITE}/#${branch.id}`,
    name: branch.name,
    alternateName: ["Justudy Library", "Justudy Co-working Space"],
    description: branch.description,
    url: SITE,
    telephone: PHONE,
    email: EMAIL,
    image: [HERO_IMAGE, LOGO_URL],
    logo: LOGO_URL,
    priceRange: "₹500–₹7200",
    paymentAccepted: "Cash, UPI, Card",
    currenciesAccepted: "INR",
    address: branch.address,
    geo: branch.geo,
    hasMap: branch.mapUrl,
    openingHoursSpecification: branch.hours,
    areaServed: NEARBY_AREAS.map((a) => ({ "@type": "Place", name: a })),
    sameAs: [],
    amenityFeature: [
      "High-speed Wi-Fi", "AC Rooms", "CCTV Security", "Power Backup",
      "Lockers", "Charging Points", "Pinboards on Every Desk",
      "Hot & Cold Water", "LED Lights", "Parking Space",
    ].map((n) => ({ "@type": "LocationFeatureSpecification", name: n, value: true })),
    keywords:
      "study library in Noida, library near me, reading room in Noida, " +
      "study library in Sector 22 Noida, study library in Sector 26 Noida, " +
      "co-working space in Noida, UPSC library Noida, AC library Noida, " +
      "24/7 library Noida",
  };
}

const BRANCH_S22 = buildLocalBusiness({
  id: "sector22",
  name: "Justudy Library – Sector 22 Noida",
  description:
    "Best 24/7 study library and co-working space in Sector 22 Noida. " +
    "Silent AC rooms, high-speed Wi-Fi, lockers, power backup, charging points, " +
    "kitchen facility and food ordering. Ideal for UPSC, SSC, CAT, GATE aspirants.",
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "2nd Floor, Moolchand Tower, Above Bank of India, Block I",
    addressLocality: "Sector 22, Noida",
    addressRegion: "Uttar Pradesh",
    postalCode: "201301",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 28.5708, longitude: 77.3265 },
  mapUrl: "https://www.google.com/maps?q=Justudy+Library+Sector+22+Noida",
  hours: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  ],
});

const BRANCH_S26 = buildLocalBusiness({
  id: "sector26",
  name: "Justudy Library – Sector 26 Noida",
  description:
    "Premium silent study library and reading room in Sector 26 Noida. " +
    "AC rooms, Wi-Fi, newspapers, network booster, lockers, charging points " +
    "and CCTV security. Open 8 AM to 10 PM daily.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "B-8, Sector 26",
    addressLocality: "Noida",
    addressRegion: "Uttar Pradesh",
    postalCode: "201301",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 28.5847, longitude: 77.3174 },
  mapUrl: "https://www.google.com/maps?q=Justudy+Library+Sector+26+Noida",
  hours: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "08:00",
      closes: "22:00",
    },
  ],
});

function buildFAQ() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildOrganization(reviews, avg) {
  const base = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE}/#org`,
    name: "Justudy Library & Co-working Space",
    url: SITE,
    logo: LOGO_URL,
    telephone: PHONE,
    email: EMAIL,
    sameAs: [],
    department: [BRANCH_S22, BRANCH_S26],
  };
  if (reviews.length > 0) {
    base.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: avg,
      reviewCount: reviews.length,
      bestRating: 5,
      worstRating: 1,
    };
    base.review = reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating || 5,
        bestRating: 5,
      },
      author: { "@type": "Person", name: r.name },
      reviewBody: r.message,
      ...(r.branch ? { itemReviewed: { "@type": "Place", name: r.branch } } : {}),
    }));
  }
  return base;
}

function buildWebsite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    url: SITE,
    name: "Justudy Library & Co-working Space",
    inLanguage: "en-IN",
    publisher: { "@id": `${SITE}/#org` },
  };
}

function buildBreadcrumb() {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Branches", item: SITE + "/#branches" },
      { "@type": "ListItem", position: 3, name: "Fees", item: SITE + "/#fees" },
      { "@type": "ListItem", position: 4, name: "Reviews", item: SITE + "/#reviews" },
      { "@type": "ListItem", position: 5, name: "Contact", item: SITE + "/#contact" },
    ],
  };
}

export default function SchemaMarkup() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data || []))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    const avg =
      reviews.length > 0
        ? (
            reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length
          ).toFixed(1)
        : "5.0";

    const schemas = [
      buildOrganization(reviews, avg),
      buildWebsite(),
      { "@context": "https://schema.org", ...BRANCH_S22 },
      { "@context": "https://schema.org", ...BRANCH_S26 },
      buildFAQ(),
      buildBreadcrumb(),
    ];

    const existing = document.getElementById(SCHEMA_ID);
    if (existing) existing.remove();
    const s = document.createElement("script");
    s.id = SCHEMA_ID;
    s.type = "application/ld+json";
    s.text = JSON.stringify(schemas);
    document.head.appendChild(s);

    return () => {
      const el = document.getElementById(SCHEMA_ID);
      if (el) el.remove();
    };
  }, [reviews]);

  return null;
}
